import { FC, useCallback, useMemo, useState } from 'react';
import { alpha, Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIButton } from '../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { ShippmentQuery, StoreQuery } from '../../../services/query';
import type { ShipmentRow } from '../autoReturnAndResend.types';
import { exportPanelToExcel } from '../autoReturnAndResend.helpers';
import { dataGridSx, renderLinkCell } from '../autoReturnAndResend.gridHelpers';
import { AutoReturnAndResendPanelHeader } from './AutoReturnAndResendPanelHeader';
import { ARAR } from '../autoReturnAndResend.classes';

interface AutoReturnAndResendResendPanelProps {
    rows: ShipmentRow[];
    onLoadingChange: (loading: boolean) => void;
}

export const AutoReturnAndResendResendPanel: FC<AutoReturnAndResendResendPanelProps> = ({ rows, onLoadingChange }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [downloadedLabels, setDownloadedLabels] = useState<Record<string, boolean>>({});

    const handleExportExcel = useCallback(() => {
        exportPanelToExcel(rows, [
            { key: 'orderNumber', label: t('general.orderNumber'), resolve: (row) => row.orderNumber ?? '-' },
            { key: 'shippingListNo', label: t('general.shippingNo'), resolve: (row) => row.shippingListNo },
        ], 'TekrarSevk');
    }, [rows, t]);

    const handleDownloadCargoLabel = useCallback(
        async (shippingListId: number | undefined, shippingListNo: string) => {
            if (!shippingListId) {
                return;
            }

            onLoadingChange(true);

            try {
                await StoreQuery.GetCargoLabelPdf({ shippingListId });
                await ShippmentQuery.UpdateShippingListStatusForReShipping({ shippingListId });

                setDownloadedLabels((prev) => ({ ...prev, [shippingListNo]: true }));
                setTimeout(() => {
                    setDownloadedLabels((prev) => {
                        const next = { ...prev };
                        delete next[shippingListNo];
                        return next;
                    });
                }, 2000);
            } catch (error) {
                console.error('Error during GetCargoLabelPdf:', error);
            } finally {
                onLoadingChange(false);
            }
        },
        [onLoadingChange]
    );

    const columns: ExtendedGridColDef[] = useMemo(
        () => [
            {
                field: 'orderNumber',
                headerName: t('general.orderNumber'),
                flex: 1,
                minWidth: 140,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(`/orderdetail?OrderNumber=${params.row.orderNumber}`, params.row.orderNumber),
            },
            {
                field: 'shippingListNo',
                headerName: t('general.shippingNo'),
                flex: 1,
                minWidth: 140,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(`/orderdetail?shippingListNo=${params.row.shippingListNo}`, params.row.shippingListNo),
            },
            {
                field: 'action',
                headerName: t('autoReturnAndResend.columns.action'),
                flex: 1,
                minWidth: 180,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => {
                    const isDownloaded = downloadedLabels[params.row.shippingListNo];

                    return (
                        <SCUIButton
                            text={isDownloaded ? t('autoReturnAndResend.actions.downloaded') : t('rpaResender.downloadCargoLabel')}
                            type="secondary"
                            sizeType="fixed"
                            size="s"
                            style={{
                                backgroundColor: isDownloaded
                                    ? theme.palette.success.light
                                    : alpha(theme.palette.primary.main, 0.08),
                                color: isDownloaded ? theme.palette.success.dark : theme.palette.primary.main,
                                borderColor: isDownloaded ? theme.palette.success.main : theme.palette.primary.main,
                            }}
                            onClick={() =>
                                handleDownloadCargoLabel(params.row.shippingListId, params.row.shippingListNo)
                            }
                        />
                    );
                },
            },
        ],
        [downloadedLabels, handleDownloadCargoLabel, t, theme]
    );

    return (
        <Box className={ARAR.panelSection}>
            <AutoReturnAndResendPanelHeader
                actions={
                    <SCUIButton
                        text={t('rpaResender.exportToExcel')}
                        type="secondary"
                        sizeType="default"
                        size="m"
                        disabled={rows.length === 0}
                        onClick={handleExportExcel}
                    />
                }
            />
            <Box className={ARAR.panelGrid} sx={dataGridSx}>
                <SCUICustomDataGrid
                    randomId
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    autoHeight
                />
            </Box>
        </Box>
    );
};
